import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { PageHeader, StatusBadge, EmptyState } from '@/components/Shared';
import { BarcodeScanner } from '@/components/BarcodeScanner';
import { money, num, dateTime } from '@/lib/format';
import { Package, Plus, Search, ScanLine, Pencil, Trash2, History, PackagePlus, PackageMinus, Barcode } from 'lucide-react';
import { toast } from 'sonner';

const emptyProduct = { name: '', brand: '', sku: '', barcode: '', category: '', quantity: 0, cost_per_unit: 0, sale_price: 0, low_stock_threshold: 10, notes: '' };

export default function Products() {
  const { isAdmin } = useAuth();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [scanOpen, setScanOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [detail, setDetail] = useState(null);
  const [adjust, setAdjust] = useState({ open: false, change: 0, reason: '' });

  const load = useCallback(async (search) => {
    setLoading(true);
    try { const { data } = await api.get('/products', { params: { q: search || undefined } }); setProducts(data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { const t = setTimeout(() => load(q), 350); return () => clearTimeout(t); }, [q, load]);

  const openNew = () => { setEditing(null); setForm(emptyProduct); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...emptyProduct, ...p }); setFormOpen(true); };

  const save = async () => {
    try {
      if (editing) { await api.put(`/products/${editing.id}`, form); toast.success('Product updated'); }
      else { await api.post('/products', form); toast.success('Product created'); }
      setFormOpen(false); load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    try { await api.delete(`/products/${p.id}`); toast.success('Deleted'); load(q); } catch (e) { toast.error('Delete failed'); }
  };

  const openDetail = async (p) => { const { data } = await api.get(`/products/${p.id}`); setDetail(data); };

  const doAdjust = async () => {
    try {
      const { data } = await api.post(`/products/${detail.id}/adjust`, { change: Number(adjust.change), reason: adjust.reason || 'Manual adjustment' });
      toast.success('Stock adjusted'); setAdjust({ open: false, change: 0, reason: '' });
      const fresh = await api.get(`/products/${detail.id}`); setDetail(fresh.data); load(q);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Adjust failed'); }
  };

  const onScan = async (code) => {
    setScanOpen(false);
    try { const { data } = await api.get('/products/lookup', { params: { code } }); openDetail(data); toast.success(`Found ${data.name}`); }
    catch { toast.error('No product found for ' + code); }
  };

  const printLabels = async () => {
    if (products.length === 0) { toast.error('No products to print'); return; }
    try {
      const res = await api.post('/barcode/labels', products.map((p) => p.id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a'); a.href = url; a.download = 'barcode-labels.pdf'; a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Barcode labels generated');
    } catch { toast.error('Failed to generate labels'); }
  };

  return (
    <div>
      <PageHeader title="Products" subtitle="Inventory catalog" testid="products-header" actions={
        <>
          <Button variant="outline" onClick={() => setScanOpen(true)} data-testid="products-table-scan-button"><ScanLine className="h-4 w-4 mr-2" /> Scan</Button>
          {isAdmin && <Button variant="outline" onClick={printLabels} data-testid="products-print-labels-button"><Barcode className="h-4 w-4 mr-2" /> Labels</Button>}
          {isAdmin && <Button onClick={openNew} data-testid="products-add-button"><Plus className="h-4 w-4 mr-2" /> Add Product</Button>}
        </>
      } />

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, SKU, barcode, brand…" className="pl-9" data-testid="products-table-search-input" />
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? <div className="p-8 text-center text-sm text-muted-foreground">Loading…</div> :
          products.length === 0 ? <div className="p-6"><EmptyState icon={Package} title="No products found" description="Add a product or import inventory to get started." /></div> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product</TableHead><TableHead>SKU</TableHead><TableHead>Category</TableHead>
                  <TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Cost</TableHead>
                  <TableHead className="text-right">Price</TableHead><TableHead>Status</TableHead><TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {products.map((p) => (
                  <TableRow key={p.id} className="cursor-pointer" data-testid="products-table-row" onClick={() => openDetail(p)}>
                    <TableCell><div className="font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.brand}</div></TableCell>
                    <TableCell className="font-mono text-xs">{p.sku}</TableCell>
                    <TableCell>{p.category || '-'}</TableCell>
                    <TableCell className="text-right tabular-nums">{num(p.quantity)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(p.cost_per_unit)}</TableCell>
                    <TableCell className="text-right tabular-nums">{money(p.sale_price)}</TableCell>
                    <TableCell><StatusBadge kind="stock" value={p.stock_status} /></TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {isAdmin && (
                        <div className="flex gap-1">
                          <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid="product-edit-button"><Pencil className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="icon" onClick={() => del(p)} data-testid="product-delete-button"><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Product form */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? 'Edit Product' : 'Add Product'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label>Product Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-form-name-input" className="mt-1.5" /></div>
            <div><Label>Brand</Label><Input value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1.5" /></div>
            <div><Label>SKU</Label><Input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} disabled={!!editing} data-testid="product-form-sku-input" className="mt-1.5" /></div>
            <div><Label>Barcode</Label><Input value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="mt-1.5" /></div>
            {!editing && <div><Label>Initial Quantity</Label><Input type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) })} className="mt-1.5" /></div>}
            <div><Label>Cost Per Unit</Label><Input type="number" step="0.01" value={form.cost_per_unit} onChange={(e) => setForm({ ...form, cost_per_unit: Number(e.target.value) })} className="mt-1.5" /></div>
            <div><Label>Sale Price</Label><Input type="number" step="0.01" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: Number(e.target.value) })} className="mt-1.5" /></div>
            <div><Label>Low Stock Threshold</Label><Input type="number" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })} className="mt-1.5" /></div>
            <div className="md:col-span-2"><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button><Button onClick={save} data-testid="product-form-save-button">{editing ? 'Save Changes' : 'Create Product'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product detail */}
      <Sheet open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          {detail && (
            <>
              <SheetHeader><SheetTitle className="font-heading">{detail.name}</SheetTitle></SheetHeader>
              <div className="mt-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><span className="text-muted-foreground">SKU</span><div className="font-mono">{detail.sku}</div></div>
                  <div><span className="text-muted-foreground">Barcode</span><div className="font-mono">{detail.barcode || '-'}</div></div>
                  <div><span className="text-muted-foreground">Brand</span><div>{detail.brand || '-'}</div></div>
                  <div><span className="text-muted-foreground">Category</span><div>{detail.category || '-'}</div></div>
                  <div><span className="text-muted-foreground">Quantity</span><div className="font-semibold">{num(detail.quantity)}</div></div>
                  <div><span className="text-muted-foreground">Status</span><div><StatusBadge kind="stock" value={detail.stock_status} /></div></div>
                  <div><span className="text-muted-foreground">Cost/Unit</span><div>{money(detail.cost_per_unit)}</div></div>
                  <div><span className="text-muted-foreground">Sale Price</span><div>{money(detail.sale_price)}</div></div>
                </div>
                {isAdmin && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setAdjust({ open: true, change: 1, reason: 'Stock in' })} data-testid="product-adjust-button"><PackagePlus className="h-4 w-4 mr-1" /> Adjust Stock</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(detail)}><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                  </div>
                )}
                <div>
                  <h4 className="font-heading font-semibold flex items-center gap-2 mb-2"><History className="h-4 w-4" /> Inventory History</h4>
                  <div className="space-y-2">
                    {(detail.movements || []).length === 0 && <p className="text-sm text-muted-foreground">No movements yet.</p>}
                    {(detail.movements || []).map((m) => (
                      <div key={m.id} className="flex items-center justify-between rounded-lg border bg-white p-2.5 text-sm">
                        <div><span className="font-medium capitalize">{m.type}</span> <span className="text-muted-foreground">· {m.reason}</span><div className="text-xs text-muted-foreground">{dateTime(m.created_at)} · {m.user_name}</div></div>
                        <div className={`font-semibold tabular-nums ${m.change >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{m.change >= 0 ? '+' : ''}{m.change} <span className="text-xs text-muted-foreground">→ {m.quantity_after}</span></div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Adjust dialog */}
      <Dialog open={adjust.open} onOpenChange={(o) => setAdjust({ ...adjust, open: o })}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">Adjust Stock</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Change (use negative to remove)</Label><Input type="number" value={adjust.change} onChange={(e) => setAdjust({ ...adjust, change: e.target.value })} data-testid="adjust-change-input" className="mt-1.5" /></div>
            <div><Label>Reason</Label><Input value={adjust.reason} onChange={(e) => setAdjust({ ...adjust, reason: e.target.value })} className="mt-1.5" /></div>
            <div className="flex gap-2"><Button variant="outline" onClick={() => setAdjust({ ...adjust, change: Number(adjust.change) - 1 })}><PackageMinus className="h-4 w-4" /></Button><Button variant="outline" onClick={() => setAdjust({ ...adjust, change: Number(adjust.change) + 1 })}><PackagePlus className="h-4 w-4" /></Button></div>
          </div>
          <DialogFooter><Button onClick={doAdjust} data-testid="adjust-save-button">Apply</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onClose={() => setScanOpen(false)} onDetected={onScan} />
    </div>
  );
}
